/**
 * rPI Porter — Auto-reply rule editor (E-102 P3-1, ADR-102 v0.3 §6)
 *
 * Route: /porter/settings/auto-reply
 *
 * Features:
 *  - List all auto-reply rules (OWNER tenant-scoped)
 *  - Create new rule via dialog
 *  - Edit existing rule via dialog
 *  - Soft-delete (active=false) with confirmation
 *
 * SCOPE: ADR-102 §6. Does NOT touch P1-7a/b (InboxListPage, InboxThreadDetailPage).
 */

import type {
  AutoReplyRule,
  AutoReplyTrigger,
  CreateAutoReplyInput,
} from '@/api/porter-auto-reply';
import { Button } from '@/components/ui';
import {
  useAutoReplyRules,
  useCreateAutoReplyRule,
  useDeleteAutoReplyRule,
  useUpdateAutoReplyRule,
} from '@/hooks/use-auto-reply-rules';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

const TEAL = '#14B8A6';

const TRIGGER_VALUES: AutoReplyTrigger[] = [
  'NEW_CONVERSATION',
  'OFF_HOURS',
  'KEYWORD',
  'NO_RESPONSE_TIMER',
  'CHANNEL_SPECIFIC',
];

// ============================================
// Rule form state
// ============================================

interface RuleFormState {
  name: string;
  trigger: AutoReplyTrigger;
  templateId: string;
  conditionJson: string; // raw JSON string for textarea
  active: boolean;
}

const DEFAULT_FORM: RuleFormState = {
  name: '',
  trigger: 'NEW_CONVERSATION',
  templateId: '',
  conditionJson: '{}',
  active: true,
};

function ruleToFormState(rule: AutoReplyRule): RuleFormState {
  return {
    name: rule.name,
    trigger: rule.trigger,
    templateId: rule.templateId,
    conditionJson: JSON.stringify(rule.conditionJson, null, 2),
    active: rule.active,
  };
}

// ============================================
// Loading skeleton
// ============================================

function LoadingSkeleton() {
  return (
    <div data-testid="auto-reply-loading" style={{ padding: '32px' }}>
      {[1, 2, 3].map(i => (
        <div
          key={i}
          style={{
            height: 64,
            background: '#1e293b',
            borderRadius: 8,
            marginBottom: 12,
            opacity: 0.6 - i * 0.1,
          }}
        />
      ))}
    </div>
  );
}

// ============================================
// Rule card
// ============================================

interface RuleCardProps {
  rule: AutoReplyRule;
  onEdit: (rule: AutoReplyRule) => void;
  onDelete: (rule: AutoReplyRule) => void;
  t: (key: string) => string;
}

function RuleCard({ rule, onEdit, onDelete, t }: RuleCardProps) {
  const triggerLabel = t(`porter.settings.autoReply.trigger.${rule.trigger}`);

  return (
    <div
      data-testid={`auto-reply-rule-${rule.id}`}
      style={{
        background: '#1e293b',
        borderRadius: 8,
        padding: '16px 20px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        opacity: rule.active ? 1 : 0.5,
      }}
    >
      {/* Status indicator */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: rule.active ? TEAL : '#64748b',
          flexShrink: 0,
        }}
        aria-label={
          rule.active
            ? t('porter.settings.autoReply.active')
            : t('porter.settings.autoReply.inactive')
        }
      />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#f8fafc', fontWeight: 500, marginBottom: 2 }}>{rule.name}</div>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>
          {triggerLabel}
          {rule.chatwootAutomationId !== null && (
            <span style={{ color: '#64748b', marginLeft: 8 }}>
              · Chatwoot #{rule.chatwootAutomationId}
            </span>
          )}
        </div>
      </div>

      {/* Sync state badge */}
      <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
        {rule.lastSyncedAt !== null ? '✓ synced' : '⟳ pending sync'}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onEdit(rule);
          }}
          aria-label={t('porter.settings.autoReply.editRule')}
        >
          {t('porter.settings.autoReply.editRule')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onDelete(rule);
          }}
          aria-label={t('porter.settings.autoReply.deleteRule')}
          style={{ color: '#ef4444' }}
        >
          {t('porter.settings.autoReply.deleteRule')}
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Rule form dialog
// ============================================

interface RuleDialogProps {
  title: string;
  initial: RuleFormState;
  onSave: (form: RuleFormState) => void;
  onCancel: () => void;
  saving: boolean;
  t: (key: string) => string;
}

function RuleDialog({ title, initial, onSave, onCancel, saving, t }: RuleDialogProps) {
  const [form, setForm] = useState<RuleFormState>(initial);
  const [jsonError, setJsonError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      JSON.parse(form.conditionJson);
    } catch {
      setJsonError('conditionJson must be valid JSON');
      return;
    }
    setJsonError(null);
    onSave(form);
  }

  return (
    <div
      data-testid="auto-reply-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: 12,
          padding: 28,
          width: 480,
          maxWidth: '90vw',
        }}
      >
        <h2 style={{ color: '#f8fafc', marginBottom: 20, fontSize: 18, fontWeight: 600 }}>
          {title}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 6 }}>
              {t('porter.settings.autoReply.form.name')}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => {
                setForm(f => ({ ...f, name: e.target.value }));
              }}
              placeholder={t('porter.settings.autoReply.form.namePlaceholder')}
              required
              style={{
                width: '100%',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: '8px 12px',
                color: '#f8fafc',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Trigger */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 6 }}>
              {t('porter.settings.autoReply.trigger.label')}
            </label>
            <select
              value={form.trigger}
              onChange={e => {
                setForm(f => ({ ...f, trigger: e.target.value as AutoReplyTrigger }));
              }}
              style={{
                width: '100%',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: '8px 12px',
                color: '#f8fafc',
                fontSize: 14,
              }}
            >
              {TRIGGER_VALUES.map(v => (
                <option key={v} value={v}>
                  {t(`porter.settings.autoReply.trigger.${v}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Template ID */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 6 }}>
              {t('porter.settings.autoReply.form.templateId')}
            </label>
            <input
              type="text"
              value={form.templateId}
              onChange={e => {
                setForm(f => ({ ...f, templateId: e.target.value }));
              }}
              placeholder={t('porter.settings.autoReply.form.templateIdPlaceholder')}
              required
              style={{
                width: '100%',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: '8px 12px',
                color: '#f8fafc',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Condition JSON */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 6 }}>
              {t('porter.settings.autoReply.form.conditionJson')}
            </label>
            <textarea
              value={form.conditionJson}
              onChange={e => {
                setForm(f => ({ ...f, conditionJson: e.target.value }));
              }}
              rows={4}
              style={{
                width: '100%',
                background: '#1e293b',
                border: jsonError !== null ? '1px solid #ef4444' : '1px solid #334155',
                borderRadius: 6,
                padding: '8px 12px',
                color: '#f8fafc',
                fontSize: 13,
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            {jsonError !== null && (
              <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{jsonError}</div>
            )}
          </div>

          {/* Active toggle */}
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              id="rule-active"
              type="checkbox"
              checked={form.active}
              onChange={e => {
                setForm(f => ({ ...f, active: e.target.checked }));
              }}
            />
            <label htmlFor="rule-active" style={{ color: '#94a3b8', fontSize: 14 }}>
              {t('porter.settings.autoReply.active')}
            </label>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
              {t('porter.settings.autoReply.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              style={{ background: TEAL, color: '#fff', border: 'none' }}
            >
              {saving ? '...' : t('porter.settings.autoReply.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// Main page
// ============================================

export function SettingsAutoReplyPage() {
  const { t } = useTranslation();
  const { data: rules, isLoading, isError } = useAutoReplyRules();
  const createMutation = useCreateAutoReplyRule();
  const updateMutation = useUpdateAutoReplyRule();
  const deleteMutation = useDeleteAutoReplyRule();

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingRule, setEditingRule] = useState<AutoReplyRule | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AutoReplyRule | null>(null);

  function handleOpenCreate() {
    setEditingRule(null);
    setDialogMode('create');
  }

  function handleOpenEdit(rule: AutoReplyRule) {
    setEditingRule(rule);
    setDialogMode('edit');
  }

  function handleOpenDelete(rule: AutoReplyRule) {
    setDeleteConfirm(rule);
  }

  function handleCloseDialog() {
    setDialogMode(null);
    setEditingRule(null);
  }

  function handleSave(form: RuleFormState) {
    let conditionJson: Record<string, unknown>;
    try {
      conditionJson = JSON.parse(form.conditionJson) as Record<string, unknown>;
    } catch {
      return;
    }

    if (dialogMode === 'create') {
      const input: CreateAutoReplyInput = {
        name: form.name,
        trigger: form.trigger,
        templateId: form.templateId,
        conditionJson,
        active: form.active,
      };
      createMutation.mutate(input, { onSuccess: handleCloseDialog });
    } else if (dialogMode === 'edit' && editingRule !== null) {
      updateMutation.mutate(
        {
          id: editingRule.id,
          input: {
            name: form.name,
            trigger: form.trigger,
            templateId: form.templateId,
            conditionJson,
            active: form.active,
          },
        },
        { onSuccess: handleCloseDialog }
      );
    }
  }

  function handleConfirmDelete() {
    if (deleteConfirm === null) return;
    deleteMutation.mutate(deleteConfirm.id, {
      onSuccess: () => {
        setDeleteConfirm(null);
      },
    });
  }

  if (isLoading) return <LoadingSkeleton />;

  if (isError) {
    return (
      <div data-testid="auto-reply-error" style={{ padding: 32, color: '#ef4444' }}>
        {t('porter.timeline.error')}
      </div>
    );
  }

  const ruleList = rules ?? [];

  return (
    <div
      data-testid="settings-auto-reply-page"
      style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 28,
        }}
      >
        <div>
          <h1 style={{ color: '#f8fafc', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
            {t('porter.settings.autoReply.title')}
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>
            {t('porter.settings.autoReply.subtitle')}
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          style={{ background: TEAL, color: '#fff', border: 'none', marginTop: 4 }}
          data-testid="add-rule-btn"
        >
          + {t('porter.settings.autoReply.addRule')}
        </Button>
      </div>

      {/* Rules list */}
      {ruleList.length === 0 ? (
        <div
          data-testid="auto-reply-empty"
          style={{
            background: '#1e293b',
            borderRadius: 8,
            padding: 32,
            textAlign: 'center',
            color: '#64748b',
          }}
        >
          {t('porter.settings.autoReply.noRules')}
        </div>
      ) : (
        <div data-testid="auto-reply-list">
          {ruleList.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDelete}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      {dialogMode !== null && (
        <RuleDialog
          title={
            dialogMode === 'create'
              ? t('porter.settings.autoReply.addRule')
              : t('porter.settings.autoReply.editRule')
          }
          initial={editingRule !== null ? ruleToFormState(editingRule) : DEFAULT_FORM}
          onSave={handleSave}
          onCancel={handleCloseDialog}
          saving={createMutation.isPending || updateMutation.isPending}
          t={t}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm !== null && (
        <div
          data-testid="delete-confirm-dialog"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: 12,
              padding: 28,
              maxWidth: 400,
            }}
          >
            <p style={{ color: '#f8fafc', marginBottom: 20 }}>
              {t('porter.settings.autoReply.confirmDelete')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleteConfirm(null);
                }}
                disabled={deleteMutation.isPending}
              >
                {t('porter.settings.autoReply.cancel')}
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                data-testid="confirm-delete-btn"
              >
                {deleteMutation.isPending ? '...' : t('porter.settings.autoReply.deleteRule')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
